import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { inputCls } from "@/components/kit";
import { MapPin, AlertTriangle } from "lucide-react";

/**
 * Champ commune adossé au référentiel national COG Insee (34 969 communes).
 * Recherche insensible aux accents, triée par population. La commune choisie
 * remonte nom + code Insee (+ flag outre-mer) dans le formulaire parent.
 * La saisie libre reste acceptée si aucune commune n'est sélectionnée.
 */
export default function CommuneField({ id, value = "", defaultCode = "", required, placeholder, onSelect }) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(defaultCode ? { nom: value, code: defaultCode, dept_nom: "", overseas: false } : null);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if ((selected && q === selected.nom) || q.length < 2) { setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, selected]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const emit = (v) => { if (onSelect) onSelect(v); };

  const choose = (c) => {
    setSelected(c);
    setQuery(c.nom);
    setOpen(false);
    emit({ city: c.nom, city_code: c.code, overseas: c.overseas });
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (!selected || v !== selected.nom) {
      setSelected(null);
      emit({ city: v, city_code: "", overseas: null });
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-results`}
        aria-autocomplete="list"
        autoComplete="off"
        required={required || undefined}
        className={inputCls}
        data-testid={id}
        value={query}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={() => { if (results.length && !selected) setOpen(true); }}
      />
      {open && results.length > 0 && (
        <ul id={`${id}-results`} role="listbox" data-testid={`${id}-results`}
            className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-ink/10 bg-white py-1 shadow-xl">
          {results.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                role="option"
                aria-selected={selected?.code === c.code}
                data-testid={`${id}-option-${c.code}`}
                onClick={() => choose(c)}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-ink/80 transition-colors hover:bg-mist"
              >
                <span className="flex items-center gap-2">
                  <MapPin size={13} className="shrink-0 text-lime-dim" />
                  <span className="font-medium text-ink">{c.nom}</span>
                  <span className="text-ink/45">{c.dept_nom}</span>
                </span>
                {c.overseas && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">Outre-mer</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <p className="mt-2 text-xs text-ink/50" data-testid={`${id}-selection`}>
          {selected.nom}{selected.dept_nom ? ` — ${selected.dept_nom}` : ""} · code Insee {selected.code}
        </p>
      )}
      {selected?.overseas && (
        <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`${id}-overseas-note`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          Outre-mer : les conditions de prise en charge (transport, hébergement, délais) sont étudiées au cas par cas — précisez votre projet dans le message.
        </p>
      )}
    </div>
  );
}
